/**
 * Button-to-Hotkey Mapping Configuration
 *
 * This file maps toolbar button IDs to their corresponding hotkey command signatures.
 *
 * WHY THIS EXISTS:
 * Toolbar buttons use commands like 'setToolActiveWithToggle' with toolGroupIds,
 * while hotkeys use 'setToolActive' with toolName. This creates a mismatch in the
 * hash-based lookup system. This mapping table explicitly links button IDs to the
 * hotkey command signatures that should be displayed in tooltips.
 *
 * MAINTENANCE:
 * When adding new toolbar buttons with keyboard shortcuts:
 * 1. Add an entry mapping the button ID to the hotkey's command structure
 * 2. Ensure the commandName and commandOptions match what's in hotkeyBindings.ts
 * 3. The hash will be generated automatically using object-hash
 */

interface HotkeyCommandSignature {
  commandName: string;
  commandOptions?: Record<string, unknown>;
}

/**
 * Maps toolbar button IDs to their corresponding hotkey command signatures.
 * The structure matches what's defined in hotkeyBindings.ts
 */
export const BUTTON_HOTKEY_MAP: Record<string, HotkeyCommandSignature> = {
  // ==================== TOOL ACTIVATION BUTTONS ====================
  // These buttons use setToolActiveWithToggle but hotkeys use setToolActive

  'Zoom': {
    commandName: 'setToolActive',
    commandOptions: { toolName: 'Zoom' },
  },

  'Pan': {
    commandName: 'setToolActive',
    commandOptions: { toolName: 'Pan' },
  },

  'StackScroll': {
    commandName: 'setToolActive',
    commandOptions: { toolName: 'StackScroll' },
  },

  'WindowLevel': {
    commandName: 'setToolActive',
    commandOptions: { toolName: 'WindowLevel' },
  },

  'Length': {
    commandName: 'setToolActive',
    commandOptions: { toolName: 'Length' },
  },

  'Angle': {
    commandName: 'setToolActive',
    commandOptions: { toolName: 'Angle' },
  },

  'RectangleROI': {
    commandName: 'setToolActive',
    commandOptions: { toolName: 'RectangleROI' },
  },

  'EllipticalROI': {
    commandName: 'setToolActive',
    commandOptions: { toolName: 'EllipticalROI' },
  },

  'CircleROI': {
    commandName: 'setToolActive',
    commandOptions: { toolName: 'CircleROI' },
  },

  'Bidirectional': {
    commandName: 'setToolActive',
    commandOptions: { toolName: 'Bidirectional' },
  },

  'ArrowAnnotate': {
    commandName: 'setToolActive',
    commandOptions: { toolName: 'ArrowAnnotate' },
  },

  'PlanarFreehandROI': {
    commandName: 'setToolActive',
    commandOptions: { toolName: 'PlanarFreehandROI' },
  },

  'CobbAngle': {
    commandName: 'setToolActive',
    commandOptions: { toolName: 'CobbAngle' },
  },

  'SplineROI': {
    commandName: 'setToolActive',
    commandOptions: { toolName: 'SplineROI' },
  },

  'LivewireContour': {
    commandName: 'setToolActive',
    commandOptions: { toolName: 'LivewireContour' },
  },

  'Probe': {
    commandName: 'setToolActive',
    commandOptions: { toolName: 'Probe' },
  },

  'Magnify': {
    commandName: 'setToolActive',
    commandOptions: { toolName: 'Magnify' },
  },

  'CalibrationLine': {
    commandName: 'setToolActive',
    commandOptions: { toolName: 'CalibrationLine' },
  },

  'UltrasoundDirectionalTool': {
    commandName: 'setToolActive',
    commandOptions: { toolName: 'UltrasoundDirectionalTool' },
  },

  'PlanarRotate': {
    commandName: 'setToolActive',
    commandOptions: { toolName: 'PlanarRotate' },
  },

  'TrackballRotate': {
    commandName: 'setToolActive',
    commandOptions: { toolName: 'TrackballRotateTool' },
  },

  'Crosshairs': {
    commandName: 'setToolActive',
    commandOptions: { toolName: 'Crosshairs' },
  },

  // ==================== SIMPLE COMMAND BUTTONS ====================
  // These buttons have matching commands between button and hotkey

  'Reset': {
    commandName: 'resetViewport',
    commandOptions: {},
  },

  'rotate-right': {
    commandName: 'rotateViewportCW',
    commandOptions: {},
  },

  'flipHorizontal': {
    commandName: 'flipViewportHorizontal',
    commandOptions: {},
  },

  'Invert': {
    commandName: 'invertViewport',
    commandOptions: {},
  },

  'Cine': {
    commandName: 'toggleCine',
    commandOptions: {},
  },

  'TagBrowser': {
    commandName: 'openDICOMTagViewer',
    commandOptions: {},
  },

  'More': {
    commandName: 'toggleMoreTools',
    commandOptions: {},
  },

  // ==================== SEGMENTATION TOOLS ====================
  // Brush and eraser tools for segmentation

  'Brush': {
    commandName: 'setToolActive',
    commandOptions: { toolName: 'CircularBrush' },
  },

  'Eraser': {
    commandName: 'setToolActive',
    commandOptions: { toolName: 'CircularEraser' },
  },

  'RectangleScissor': {
    commandName: 'setToolActive',
    commandOptions: { toolName: 'RectangleScissor' },
  },

  'CircleScissor': {
    commandName: 'setToolActive',
    commandOptions: { toolName: 'CircleScissor' },
  },

  'SphereScissor': {
    commandName: 'setToolActive',
    commandOptions: { toolName: 'SphereScissor' },
  },

  'RectangleROIThreshold': {
    commandName: 'setToolActive',
    commandOptions: { toolName: 'RectangleROIThreshold' },
  },
};

/**
 * Format hotkey keys for display in tooltips
 * Examples:
 *   ['z'] → 'Z'
 *   ['ctrl+z'] → 'Ctrl+Z'
 *   ['shift+alt+a'] → 'Shift+Alt+A'
 */
export function formatHotkeyForDisplay(keys: string[]): string | null {
  if (!keys || keys.length === 0) {
    return null;
  }

  // Use the first key combination if multiple exist
  const keyString = keys[0];

  // Split by + and capitalize each part
  const parts = keyString.split('+').map(part => {
    // Capitalize first letter of each key
    return part.charAt(0).toUpperCase() + part.slice(1);
  });

  return parts.join('+');
}
